import type { Box, Resolution } from "@/features/Editor/Canvas/canvas-geometry";

const SNAP_DISTANCE_SCREEN_PX = 5;
// Snapped positions are rounded afterwards, so a line can end up half a pixel off its target.
const ALIGNMENT_TOLERANCE = 0.5;

// A line to snap to, spanning from..to on the other axis.
export type SnapTarget = {
  position: number;
  from: number;
  to: number;
};

export type SnapTargets = {
  x: SnapTarget[];
  y: SnapTarget[];
};

export type Guideline = {
  orientation: "vertical" | "horizontal";
  position: number;
  from: number;
  to: number;
};

const getXLines = (box: Box) => [box.x, box.x + box.w / 2, box.x + box.w];
const getYLines = (box: Box) => [box.y, box.y + box.h / 2, box.y + box.h];

// Edges and centers of the given boxes and of the canvas.
export function getSnapTargets(
  boxes: Box[],
  resolution: Resolution,
): SnapTargets {
  const allBoxes = [
    { x: 0, y: 0, w: resolution.width, h: resolution.height },
    ...boxes,
  ];

  return {
    x: allBoxes.flatMap((box) =>
      getXLines(box).map((position) => ({
        position,
        from: box.y,
        to: box.y + box.h,
      })),
    ),
    y: allBoxes.flatMap((box) =>
      getYLines(box).map((position) => ({
        position,
        from: box.x,
        to: box.x + box.w,
      })),
    ),
  };
}

// The offset that moves the box's closest edge or center onto a target, per axis.
export function getSnapOffset(box: Box, targets: SnapTargets, scale: number) {
  return {
    dx: getAxisSnapOffset(getXLines(box), targets.x, scale),
    dy: getAxisSnapOffset(getYLines(box), targets.y, scale),
  };
}

// The closest target within snap distance of the line, or null.
export function snapLine(line: number, targets: SnapTarget[], scale: number) {
  let snap: { offset: number; distance: number } | null = null;

  for (const { position } of targets) {
    const distance = Math.abs(position - line);

    if (
      distance <= SNAP_DISTANCE_SCREEN_PX / scale &&
      (!snap || distance < snap.distance)
    ) {
      snap = { offset: position - line, distance };
    }
  }

  return snap;
}

// Lines through the targets the box's edges or center are aligned with, spanning both
// the box and the targets.
export function getGuidelines(box: Box, targets: SnapTargets): Guideline[] {
  return [
    ...getAxisGuidelines(
      "vertical",
      getXLines(box),
      targets.x,
      box.y,
      box.y + box.h,
    ),
    ...getAxisGuidelines(
      "horizontal",
      getYLines(box),
      targets.y,
      box.x,
      box.x + box.w,
    ),
  ];
}

function getAxisSnapOffset(
  lines: number[],
  targets: SnapTarget[],
  scale: number,
) {
  let closest: { offset: number; distance: number } | null = null;

  for (const line of lines) {
    const snap = snapLine(line, targets, scale);

    if (snap && (!closest || snap.distance < closest.distance)) {
      closest = snap;
    }
  }

  return closest?.offset ?? 0;
}

function getAxisGuidelines(
  orientation: Guideline["orientation"],
  lines: number[],
  targets: SnapTarget[],
  from: number,
  to: number,
) {
  const guidelines = new Map<number, Guideline>();

  for (const line of lines) {
    for (const target of targets) {
      if (Math.abs(target.position - line) > ALIGNMENT_TOLERANCE) {
        continue;
      }

      const guideline = guidelines.get(target.position) ?? {
        orientation,
        position: target.position,
        from,
        to,
      };

      guidelines.set(target.position, {
        ...guideline,
        from: Math.min(guideline.from, target.from),
        to: Math.max(guideline.to, target.to),
      });
    }
  }

  return [...guidelines.values()];
}
